import React, { useState } from 'react'
import { Button } from 'transfix-ui/components/Button'
import ChipsDisplayed, {
  IChipsDisplayedProps,
} from 'transfix-ui/components/ChipsTextInput/components/ChipsDisplayed'
import { TextField, ITextFieldProps } from 'transfix-ui/components/TextField'
import { makeStyles } from 'transfix-ui/core'
import { PlusIcon } from 'transfix-icons'

export interface IChipsTextInputProps {
  /**
   * Function that takes all the labels of chips, both shown or hidden by 'view more'
   * Mainly useful for updating a state variable uses values from the chips
   * It runs each time a chip is added or removed
   */
  buttonText?: string
  onChange?: (newValue: string[]) => void
  onInputChange?: () => void
  ChipsDisplayedProps?: Partial<IChipsDisplayedProps>
  TextFieldProps?: Partial<
    Omit<ITextFieldProps, 'onChange' | 'onBlur' | 'value' | 'error' | 'validationMessage'>
  >
  children?: React.ReactNode
  startLabels?: string[]
  validateNewInput?: (newValue: string) => Promise<[boolean, Error | null]>
}

const inputStyles = makeStyles(theme => ({
  box: {
    paddingBottom: theme.spacing(1),
    margin: 0,
  },
  inputContainer: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'flex-start',
  },
  button: {
    flex: '200px',
    height: 35,
    marginTop: 19,
  },
}))

const ChipsTextInput = ({
  buttonText,
  children,
  onChange,
  onInputChange,
  startLabels = [],
  validateNewInput,
  ChipsDisplayedProps = {},
  TextFieldProps = {},
}: IChipsTextInputProps) => {
  const inputClass = inputStyles()
  const [validationError, setValidationError] = useState<string | null>(null)
  const [value, setInputValue] = useState<string>('')
  const [chipLabels, setChipLabels] = useState<IChipsDisplayedProps['chipLabels']>(startLabels)

  const reset = () => {
    if (onInputChange) onInputChange()
    setValidationError(null)
  }

  const setChipLabelsHandler = (labels: string[]) => {
    setChipLabels(labels)
    if (onChange) {
      onChange(labels)
    }
  }

  const updateChips = async (input: string, chipLabels: string[]) => {
    const newChip = input.trim()

    if (newChip.length === 0) return

    if (validateNewInput) {
      const [shouldIAddIt, error] = await validateNewInput(newChip)

      if (!shouldIAddIt && error) {
        setValidationError(error.message)
        return
      }
    }

    // we only want unique values - this eliminates duplicates
    setChipLabelsHandler([...new Set([...chipLabels, newChip])])
    setInputValue('')
  }

  const textInputChangeHandler: ITextFieldProps['onChange'] = e => {
    reset()
    setInputValue(e.target.value)
  }

  const textInputBlurHandler: ITextFieldProps['onBlur'] = e => {
    if (!buttonText) {
      updateChips(e.target.value, chipLabels)
    }
  }

  const keyDownHandler: React.KeyboardEventHandler<HTMLDivElement> = e => {
    if (e.key === 'Enter') {
      updateChips(value, chipLabels)
    }
  }

  const clickHandler = () => {
    reset()
    updateChips(value, chipLabels)
  }

  return (
    <div data-test='chip-text-input' className={inputClass.box}>
      <div className={inputClass.inputContainer}>
        <TextField
          {...TextFieldProps}
          onChange={textInputChangeHandler}
          onBlur={textInputBlurHandler}
          value={value}
          data-test='input-text-box'
          error={Boolean(validationError)}
          validationMessage={validationError || undefined}
          onKeyDown={keyDownHandler}
        />
        {buttonText && (
          <Button className={inputClass.button} startIcon={<PlusIcon />} onClick={clickHandler}>
            {buttonText}
          </Button>
        )}
      </div>
      {children || null}
      {chipLabels.length > 0 && (
        <ChipsDisplayed
          {...ChipsDisplayedProps}
          chipCharacterLimit={ChipsDisplayedProps.chipCharacterLimit || 20}
          chipsDisplayedLimit={ChipsDisplayedProps.chipsDisplayedLimit || 20}
          displayLimitIncrease={ChipsDisplayedProps.displayLimitIncrease || 20}
          chipLabels={chipLabels}
          onSetChipLabels={setChipLabelsHandler}
        />
      )}
    </div>
  )
}

export default ChipsTextInput
